import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import {
  type DemoClearResult,
  useDemoDataClear
} from "../projects/useDemoDataClear";

export interface PlanDemoDataResult {
  condition_revision: number;
  requirement_count: number;
  allocation_count: number;
  total_lessons: number;
  cleared_plan_count: number;
  timetable_preserved: boolean;
}

export function planDemoMessage(result: PlanDemoDataResult): string {
  return `任课计划模拟数据已填入：${result.requirement_count} 项班级需求、${result.allocation_count} 项教师分配，共 ${result.total_lessons} 节周课时。请检查后生成详细计划。`;
}

export function usePlanDemoData(
  projectId: string,
  expectedConditionRevision: number,
  onApplied: (result: PlanDemoDataResult) => void,
  beforeRefresh?: (result: PlanDemoDataResult) => void,
  onCleared?: (result: DemoClearResult) => void
) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: (confirmReplace: boolean) =>
      apiRequest<PlanDemoDataResult>(
        `/projects/${projectId}/planning/demo-data`,
        {
          method: "POST",
          body: JSON.stringify({
            expected_condition_revision: expectedConditionRevision,
            confirm_replace: confirmReplace
          })
        }
      ),
    onSuccess: async (result) => {
      setConfirmOpen(false);
      beforeRefresh?.(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["planning-draft", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      ]);
      onApplied(result);
    },
    onError: (error, confirmReplace) => {
      if (
        !confirmReplace &&
        error instanceof ApiError &&
        error.code === "CONFIRMATION_REQUIRED"
      ) {
        setConfirmOpen(true);
      }
    }
  });
  const clear = useDemoDataClear(
    projectId,
    "PLANNING",
    (result) => onCleared?.(result)
  );

  return {
    confirmOpen,
    mutation,
    start: () => {
      mutation.reset();
      mutation.mutate(false);
    },
    closeConfirm: () => {
      mutation.reset();
      setConfirmOpen(false);
    },
    confirm: () => mutation.mutate(true),
    clear,
    visibleError:
      mutation.isError &&
      !confirmOpen &&
      !(mutation.error instanceof ApiError &&
        mutation.error.code === "CONFIRMATION_REQUIRED")
        ? mutation.error
        : null,
    confirmError:
      mutation.isError &&
      !(mutation.error instanceof ApiError &&
        mutation.error.code === "CONFIRMATION_REQUIRED")
        ? mutation.error
        : null
  };
}
